// ** React Imports
import {
	useContext,
	useState,
	useRef,
	useEffect,
	useMemo,
	useCallback,
} from "react";

// ** Redux & Store & Actions
import { useSelector, useDispatch } from "react-redux";
import { setUserCountryFromIp } from "store/modules/global";

// ** Third Party Components
import { useTranslation } from "react-i18next";
import loadable from "@loadable/component";

// ** Custom Components
import Block from "components/layout/Block";
import Dropdown from "components/layout/Dropdown";
import Loader from "components/layout/Loader";
import { DropdownTriggerContext } from "components/layout/Dropdown/DropdownTriggerContext";

// ** Location AutoComplete Components
import DistanceSelector from "./components/DistanceSelector";
import LocationActivator from "./components/LocationActivator";
import ModalRegionItem from "./components/ModalRegionItem";
import ModalCountryItem from "./components/ModalCountryItem";
import ModalCityItem from "./components/ModalCityItem";
import ModalEnterCityLabelItem from "./components/ModalEnterCityLabelItem";
import ModalNoResultsItem from "./components/ModalNoResultsItem";
import OtherModalItem from "./components/OtherModalItem";

// ** Location AutoComplete Services
import { generateSelectedLocationText } from "./services/generateSelectedLocationText";

// ** Custom Hooks
import useOpenClose from "hooks/useOpenClose";
import useUpdateEffect from "hooks/useUpdateEffect";
import useDetectKeyboardOpen from "hooks/useDetectKeyboardOpen";

// ** Utils & Helpers
import { publish, subscribe, unsubscribe } from "helpers/events";
import { ACTUAL_OPEN_SELECTOR } from "helpers/eventsList";
import api from "helpers/ApiClient";
import slug from "../../../utils/slug";

// ** Contexts
import deviceTypeContext from "contexts/deviceTypeContext";

// ** Styled Components
import SearchInput from "../DropdownSelectors/components/SearchInput";
import { StyledSelectorLabel } from "../DropdownSelectors/components/SelectorLabel.styled.";

// ** Lazy Loaded Components
const OtherLocations = loadable(
	() => import("./components/OtherLocations/OtherLocations"),
	{ ssr: false }
);

// #####################################################

const LocationAutocomplete = ({
	selectorKey,
	location,
	onAccept: onAcceptHook,
	label,
	withDistance,
	inputComponent,
	inputComponentProps,
	extend,
	noPad,
	noMaxWidth,
	noBold,
	withEnterTheCityLabel,
	minWidth,
	subscribeActualOpenSelectorEvent,
	useLayerOptions,
	onlyCities,
	noOther,
	customLayerStyle,
	hideLabel,
	selectorProps,
	isBan,
	alwaysLight,
	withGeolocation,
	customMobileLayerStyle,
	notCleanInputInUseEffect = false,
	maxLayerHeight = 350,
	isLanding,
	closeWithoutEnableScroll = false,
}) => {
	const { t } = useTranslation([
		"common",
		"countries",
		"filtersAndSelectors",
		"regions",
		"pages",
	]);
	const dispatch = useDispatch();
	const { isPhone } = useContext(deviceTypeContext);

	const { isOpen, close, open } = useOpenClose();
	const { isKeyboardOpen, visualViewportHeight } = useDetectKeyboardOpen();

	const isAuth = useSelector((state) => state.globalCommon.isAuth);
	const userLocationDataCountryId = useSelector(
		(state) => state?.globalUser?.country
	);
	const userLocationDataUserCountryFromIp = useSelector(
		(state) => state?.global?.userCountryFromIp
	);

	const language = useSelector((state) => state.globalCommon.language);

	const geolocationInitialData = useSelector(
		(state) => state.global.geolocation.initialData.data
	);
	const geolocationInitialDataLoading = useSelector(
		(state) => state.global.geolocation.initialData.loading
	);

	// ** Wybrana lokalizacja
	const [value, setValue] = useState("");
	const [beforeFocusLocationValue, setBeforeFocusLocationValue] =
		useState("");

	// ** Lista wyników wyszukiwania
	const [result, setResult] = useState({
		city: [],
		region: [],
		country: [],
	});
	const isSelected = useMemo(() => {
		return Boolean(value && value.length > 0);
	}, [value]);

	const [isOther, setIsOther] = useState(false);
	const [initialResultLoading, setInitialResultLoading] = useState(true);

	// ** Referencja do wrappera na podstawie którego jest obliczna szerokość otwieranego dropdown'u.
	const wrapperRef = useRef(null);
	const controllerRef = useRef(new AbortController());
	const controller = controllerRef?.current;
	const showDistance = withDistance && Boolean(location?.cityName);
	const isLocationItemClicked = useRef(false);

	const extendedClose = ({ insideUseEffect = false } = {}) => {
		if (insideUseEffect || closeWithoutEnableScroll) {
			close();
		} else {
			close(() => {
				if (
					document.body.style.overflowY === "hidden" &&
					!customMobileLayerStyle
				) {
					document.body.style.overflowY = "scroll";
				}
			});
		}
	};

	const extendedOpen = ({ insideUseEffect = false } = {}) => {
		if (isBan) {
			return;
		}
		if (insideUseEffect) {
			open();
		} else {
			open(() => {
				if (
					!isOpen &&
					isPhone &&
					!customMobileLayerStyle &&
					document.body.style.overflowY !== "hidden"
				) {
					document.body.style.overflowY = "hidden";
				}
			});
		}
	};

	// ** Dropdown opcje konfiguracyjne
	const useLayerOptionsFinal = {
		onOutsideClick: () => {
			/* MARCIN_P (DZ-1912): Without "!isPhone" there is a bug when opening on mobile */
			if (!isPhone) {
				// ** Przeniesione z useEffect - ponieważ wówczas zamykało dropdown po utracie focus'a - również w  przypadku kliknięcia lewym przyckiem myszy,
				extendedClose();
				setValue(beforeFocusLocationValue);
				setBeforeFocusLocationValue("");
			}
		},
		onParentClose: () => {
			extendedClose();
		},
		overflowContainer: true,
		...useLayerOptions,
	};

	const customLayerStyleFinal = {
		maxHeight: `${maxLayerHeight}px`,
		zIndex: isPhone ? 1000 : 499,
		...customLayerStyle,
	};

	const currentCityLocationObject = useMemo(() => {
		return (location && location.cityId && location.cityId !== "any") ||
			location?.cityName
			? {
					country: location.country,
					id: location.cityId,
					name: location.cityName,
					region_id: location.regionId,
					region_name: location.regionName,
					url: location.url,
					isSelected: true,
			  }
			: undefined;
	}, [
		location?.cityId,
		location?.country,
		location?.regionId,
		location?.url,
	]);

	const currentCityLocation = useMemo(() => {
		return currentCityLocationObject ? [currentCityLocationObject] : [];
	}, [currentCityLocationObject]);

	const currentRegionLocationObject = useMemo(() => {
		return location &&
			location.regionId &&
			location.regionId !== "any" &&
			(location.cityId === "any" ||
				location.cityId === undefined ||
				location.cityId === null)
			? {
					country: location.country,
					id: location.regionId,
					name: location.regionName,
					url: location.url,
			  }
			: undefined;
	}, [
		location?.cityId,
		location?.country,
		location?.regionId,
		location?.url,
	]);

	const currentRegionLocation = useMemo(() => {
		return currentRegionLocationObject ? [currentRegionLocationObject] : [];
	}, [currentRegionLocationObject]);

	const currentCountryLocationObject = useMemo(() => {
		return location &&
			location.country &&
			location.country !== "any" &&
			location.country !== "all" &&
			(location.cityId === "any" ||
				location.cityId === undefined ||
				location.cityId === null) &&
			(location.regionId === "any" ||
				location.regionId === undefined ||
				location.regionId === null)
			? {
					id: location.country,
					url: location.url,
			  }
			: undefined;
	}, [
		location?.cityId,
		location?.country,
		location?.regionId,
		location?.url,
	]);

	const currentCountryLocation = useMemo(() => {
		return currentCountryLocationObject
			? [currentCountryLocationObject]
			: [];
	}, [currentCountryLocationObject]);

	// ** Funkcja obsługujaca wybór konkretnej lokalizacji
	const handleLocationSelect = ({
		cityId = null,
		cityName = null,
		regionId = null,
		regionName = null,
		country = null,
		countryName = null,
		distance = 50,
		url = false,
		showError = false,
		accept = false,
		isSelected = false,
	}) => {
		const selectedLocationText = generateSelectedLocationText({
			cityId,
			cityName,
			regionId,
			regionName,
			country,
			countryName,
			t,
		});

		setBeforeFocusLocationValue("");
		setValue(selectedLocationText);

		if (!isSelected && accept && typeof onAcceptHook === "function") {
			onAcceptHook({
				cityId,
				cityName,
				regionId,
				regionName,
				country,
				countryName,
				distance,
				url,
				showError,
			});
		} else if (isSelected && accept && typeof onAcceptHook === "function") {
			onAcceptHook({
				cityId: null,
				cityName: null,
				regionId: null,
				regionName: null,
				country: null,
				countryName: null,
				distance: 50,
				url: null,
				showError: false,
			});
		}
	};

	// ** Funkcja Renderuje Listę Miast Spełniajacych Kryteria Wyszukiwania
	const renderModalCities = () => {
		return result.city.map((city) => {
			return (
				<ModalCityItem
					key={city.id}
					city={city}
					close={extendedClose}
					location={location}
					handleLocationSelect={handleLocationSelect}
					alwaysLight={alwaysLight}
					isSelected={city.isSelected}
					isRegister={inputComponentProps?.register}
				/>
			);
		});
	};

	const renderOtherModalItem = useCallback(() => {
		return !noOther ? (
			<OtherModalItem
				close={extendedClose}
				handleChangeToOtherLocations={handleChangeToOtherLocations}
				alwaysLight={alwaysLight}
			/>
		) : null;
	}, [alwaysLight, noOther]);

	// ** Funkcja Renderuje Region i Kraj Spełniajace Kryteria Wyszukiwania
	const renderModalExtend = useCallback(() => {
		return (
			<>
				{result.region && result.region.length > 0 && (
					<>
						{result.region.map((region) => {
							const regionSlug = region.url
								? region.url.split("/")[1]
								: "";

							return (
								<ModalRegionItem
									key={region.id}
									region={region}
									close={extendedClose}
									handleLocationSelect={handleLocationSelect}
									alwaysLight={alwaysLight}
									isSelected={
										(regionSlug === location?.regionId ||
											region.name ===
												location?.regionName) &&
										(location?.cityId === null ||
											location?.cityId === "any")
									}
								/>
							);
						})}
					</>
				)}
				{result?.country && result?.country?.length > 0 && (
					<>
						{result.country.map((country) => {
							return (
								<ModalCountryItem
									key={country.id}
									country={country}
									close={extendedClose}
									handleLocationSelect={handleLocationSelect}
									alwaysLight={alwaysLight}
									isSelected={
										country.id === location?.country &&
										!(
											location?.regionId &&
											location?.regionId !== "any"
										) &&
										!(
											location?.cityId &&
											location?.cityId !== "any"
										) &&
										!location?.cityName
									}
								/>
							);
						})}
					</>
				)}
			</>
		);
	}, [
		location?.cityId,
		location?.regionId,
		location?.country,
		result?.country,
		result?.region,
	]);

	const onFocus = (e) => {
		e.stopPropagation();
		isLocationItemClicked.current = false;
		setBeforeFocusLocationValue(value);
		setValue("");
		extendedOpen();
	};

	const onGeolocationChange = () => {
		setInitialResultLoading(false);
		if (
			withGeolocation &&
			geolocationInitialData &&
			geolocationInitialData.city.length
		) {
			const cities = (geolocationInitialData.city || [])
				.map((city) => {
					if (location?.url && city.url === location?.url) {
						return {
							...city,
							isSelected: true,
						};
					}
					if (
						currentCityLocationObject?.id &&
						`${city.id}` === currentCityLocationObject.id
					) {
						return {
							...city,
							isSelected: true,
						};
					}
					return city;
				})
				.filter((initialLocation) => {
					if (location?.url) {
						return initialLocation.url !== location?.url;
					}
					if (currentCityLocationObject?.id) {
						return (
							`${initialLocation.id}` !==
							currentCityLocationObject.id
						);
					}
					return true;
				})
				.sort((a, b) => {
					return a.isSelected === b.isSelected
						? 0
						: a.isSelected
						? -1
						: 1;
				});

			setResult((prev) => ({
				...prev,
				city: [...currentCityLocation, ...cities],
				region: onlyCities
					? []
					: currentRegionLocationObject?.name ===
					  geolocationInitialData.region[0].name
					? [...currentRegionLocation]
					: [
							...currentRegionLocation,
							...geolocationInitialData.region,
					  ],
				country: onlyCities
					? []
					: geolocationInitialData?.country
					? currentCountryLocationObject?.id ===
					  geolocationInitialData.country[0].id
						? [...geolocationInitialData.country]
						: [
								...currentCountryLocation,
								...geolocationInitialData.country,
						  ]
					: [],
			}));
		}
	};

	// ** Funkcja obsługująca wpisywanie treści w pole "Miejscowość"
	const handleInputChange = (e) => {
		controller.abort();
		controllerRef.current = new AbortController();

		const value = e.target.value;

		if (
			e.target.value === "" &&
			withGeolocation &&
			geolocationInitialData &&
			geolocationInitialData.city.length
		) {
			setInitialResultLoading(false);

			const cities = (geolocationInitialData.city || []).map((city) => {
				if (location?.url && city.url === location?.url) {
					return {
						...city,
						isSelected: true,
					};
				}
				if (
					currentCityLocationObject?.id &&
					`${city.id}` === currentCityLocationObject.id
				) {
					return {
						...city,
						isSelected: true,
					};
				}
				return city;
			});

			setResult((prev) => ({
				...prev,
				city: cities,
				region: onlyCities
					? []
					: currentRegionLocationObject?.name ===
					  geolocationInitialData.region[0].name
					? [...currentRegionLocation]
					: [
							...currentRegionLocation,
							...geolocationInitialData.region,
					  ],
				country: onlyCities
					? []
					: geolocationInitialData?.country
					? currentCountryLocationObject?.id ===
					  geolocationInitialData.country[0].id
						? [...geolocationInitialData.country]
						: [
								...currentCountryLocation,
								...geolocationInitialData.country,
						  ]
					: [],
			}));
		} else {
			if (e.target.value === "") {
				fetchInitialResult(userLocationDataCountryId || "pl");
			} else {
				api.post(
					`/datezone/autocomplete`,
					{
						value: e.target.value,
						extend: true,
						country: null,
					},
					{
						signal: controllerRef?.current?.signal,
					}
				)
					.then(({ data } = {}) => {
						if (data) {
							const cities = (data?.city || [])
								.map((city) => {
									if (
										city.url === location?.url ||
										`${city.id}` === location?.cityId
									) {
										return {
											...city,
											isSelected: true,
										};
									}
									return city;
								})
								.sort((a, b) => {
									return a.isSelected === b.isSelected
										? 0
										: a.isSelected
										? -1
										: 1;
								});

							setResult({
								city: cities,
								region:
									data?.region && !onlyCities
										? data?.region
										: [],
								country:
									data?.country && !onlyCities
										? data?.country
										: [],
							});
						} else {
							setResult({
								city: [],
								region: [],
								country: [],
							});
						}
					})
					.catch((e) => {
						if (e.message === "canceled") {
							return null;
						}

						// TODO: Error handler in extension.
						// eslint-disable-next-line no-console
						console.error(e);
					});
			}
		}
		const valueArray = value.split("");
		const valueArraySlug = valueArray.filter((value) => {
			if (value === " " || slug(value, "")) {
				return value;
			}
		});
		const valueFiltered = valueArraySlug.join("");
		setValue(value);
		if (valueFiltered.length >= 1) {
			if (!isOpen) {
				extendedOpen();
			}
		}
	};

	const handleChangeToOtherLocations = () => {
		setIsOther(true);
		handleLocationSelect({
			city: null,
			region: null,
			country: null,
			accept: true,
		});
	};

	const fetchInitialResult = (countryId) => {
		api.post(
			`/datezone/autocomplete`,
			{
				value: "",
				extend: true,
				country: countryId,
			},
			{
				signal: controllerRef?.current?.signal,
			}
		)
			.then(({ data } = {}) => {
				setInitialResultLoading(false);
				if (data) {
					const initialLocations = (data?.city || [])
						.filter((initialLocation) => {
							if (location?.url) {
								return initialLocation.url !== location?.url;
							}
							if (currentCityLocationObject?.id) {
								return (
									`${initialLocation.id}` !==
									currentCityLocationObject.id
								);
							}
							return true;
						})
						.sort((a, b) => {
							return a.isSelected === b.isSelected
								? 0
								: a.isSelected
								? -1
								: 1;
						});

					setResult({
						city: [...currentCityLocation, ...initialLocations],
						region: onlyCities
							? []
							: data?.region
							? currentRegionLocationObject?.name ===
							  data.region[0].name
								? [...data.region]
								: [...currentRegionLocation, ...data.region]
							: [],
						country: onlyCities
							? []
							: data?.country
							? currentCountryLocationObject?.id ===
							  data.country[0].id
								? [...data.country]
								: [...currentCountryLocation, ...data.country]
							: [],
					});
				} else {
					setResult({
						city: [],
						region: [],
						country: [],
					});
				}
			})
			.catch((e) => {
				setInitialResultLoading(false);

				if (e.message === "canceled") {
					return null;
				}
			});
	};

	useEffect(() => {
		if (location) {
			handleLocationSelect(location);
		}
	}, [location?.url, language]);

	useEffect(() => {
		if (
			withGeolocation &&
			geolocationInitialData &&
			geolocationInitialData.city.length
		) {
			setInitialResultLoading(false);

			const cities = (geolocationInitialData.city || [])
				.map((city) => {
					if (
						city.url === location?.url ||
						`${city.id}` === location?.cityId
					) {
						return {
							...city,
							isSelected: true,
						};
					}
					return city;
				})
				.filter((initialLocation) => {
					if (location?.url) {
						return initialLocation.url !== location?.url;
					}
					if (currentCityLocationObject?.id) {
						return (
							`${initialLocation.id}` !==
							currentCityLocationObject.id
						);
					}
					return true;
				})
				.sort((a, b) => {
					return a.isSelected === b.isSelected
						? 0
						: a.isSelected
						? -1
						: 1;
				});

			setResult((prev) => ({
				...prev,
				city: [...currentCityLocation, ...cities],
				region: onlyCities
					? []
					: currentRegionLocationObject?.name ===
					  geolocationInitialData?.region[0]?.name
					? [...geolocationInitialData.region]
					: [
							...currentRegionLocation,
							...geolocationInitialData.region,
					  ],
				country: onlyCities
					? []
					: geolocationInitialData?.country
					? currentCountryLocationObject?.id ===
					  geolocationInitialData.country[0].id
						? [...geolocationInitialData.country]
						: [
								...currentCountryLocation,
								...geolocationInitialData.country,
						  ]
					: [],
			}));
		} else if (isOpen && isAuth && !geolocationInitialDataLoading) {
			fetchInitialResult(userLocationDataCountryId || "pl");
		} else if (isOpen && !isAuth && !geolocationInitialDataLoading) {
			if (!userLocationDataUserCountryFromIp) {
				const storedCountryId = JSON.parse(
					sessionStorage.getItem("userCountryFromIp")
				);
				if (storedCountryId) {
					fetchInitialResult(storedCountryId);
					dispatch(setUserCountryFromIp(storedCountryId));
				} else {
					api.get(`/system/country`)
						.then(({ data }) => {
							if (data?.country) {
								fetchInitialResult(data.country);

								dispatch(setUserCountryFromIp(data.country));
								sessionStorage.setItem(
									"userCountryFromIp",
									JSON.stringify(data.country)
								);
							}
						})
						.catch(() => {
							fetchInitialResult("pl");
						});
				}
			} else if (userLocationDataUserCountryFromIp) {
				fetchInitialResult(userLocationDataUserCountryFromIp);
			}
		}

		if (!isOpen) {
			setResult({
				city: [],
				region: [],
				country: [],
			});

			setInitialResultLoading(true);
		}
	}, [isOpen]);

	useEffect(() => {
		if (
			!isOpen &&
			beforeFocusLocationValue &&
			value === "" &&
			notCleanInputInUseEffect
		) {
			setValue(beforeFocusLocationValue);
			setBeforeFocusLocationValue("");
		}
	}, [isOpen, beforeFocusLocationValue, value, notCleanInputInUseEffect]);

	// ** Efekt publikujący CustomEvent 'ACTUAL_OPEN_SELECTOR'
	// ** z 'id' (label) aktualnie otwartego selektora.
	useEffect(() => {
		if (isOpen) {
			publish(ACTUAL_OPEN_SELECTOR, { selectorKey });
		}
	}, [isOpen, isPhone]);

	// ** Efekt subskrybujący event 'ACTUAL_OPEN_SELECTOR'
	// ** W przypadku gdy 'id' nowo otwartego selektora nie zgada się z 'id'
	// ** bieżacego komponentu jest on zamykany.
	useEffect(() => {
		if (subscribeActualOpenSelectorEvent) {
			subscribe(ACTUAL_OPEN_SELECTOR, (event) => {
				if (event.detail.selectorKey !== selectorKey) {
					extendedClose({ insideUseEffect: true });
				}
			});
		}

		return () => {
			unsubscribe(ACTUAL_OPEN_SELECTOR);
		};
	}, []);

	// **
	useUpdateEffect(() => {
		if (
			withGeolocation &&
			geolocationInitialData &&
			geolocationInitialData.city.length
		) {
			onGeolocationChange();
		}
	}, [
		geolocationInitialData?.city,
		geolocationInitialData?.region,
		geolocationInitialData?.country,
	]);

	// #####################################################

	return !isOther ? (
		<Block
			flex
			style={{
				width: isPhone ? "100%" : "unset",
			}}
		>
			<Block
				fullWidth
				style={{
					minWidth:
						customMobileLayerStyle || isPhone ? "150px" : null,
				}}
				ref={wrapperRef}
			>
				<Dropdown
					isOpen={isOpen}
					close={(insideUseEffect) => {
						extendedClose(insideUseEffect);
					}}
					customMobileLayerStyle={{
						display: "flex",
						flexDirection: "column",
						justifyContent: "center",
						left: customMobileLayerStyle ? "2rem" : "1rem",
						right: customMobileLayerStyle ? "2rem" : "1rem",
					}}
					useLayerOptions={useLayerOptionsFinal}
					customLayerStyle={customLayerStyleFinal}
					layerWidth={wrapperRef?.current?.clientWidth}
					mobileTitle={label}
					withMobileVersion={true}
					alwaysLight={alwaysLight}
					isKeyboardOpen={isKeyboardOpen}
					visualViewportHeight={visualViewportHeight}
					isParentComponent={!customMobileLayerStyle}
					isSingleDropdown={true}
					isMultiDropdown={customMobileLayerStyle}
					autoBorder={true}
					isLanding={isLanding}
					menuContent={({ layerSide }) =>
						isOpen && (
							<Block
								full
								width="100%"
								style={{
									height: "100%",
									maxHeight: isPhone
										? "unset"
										: `${maxLayerHeight}px`,
								}}
								onMouseDown={() => {
									isLocationItemClicked.current = true;
								}}
							>
								<Block
									full
									style={{
										overflow: "auto",
										height: "100%",
										maxHeight: isPhone
											? "unset"
											: `${maxLayerHeight - 10}px`,
									}}
									alwaysLight={alwaysLight}
								>
									{layerSide === "top" && !isPhone && (
										<Block pl={4} pt={2}>
											<StyledSelectorLabel>
												{label}
											</StyledSelectorLabel>
										</Block>
									)}
									{!initialResultLoading ? (
										<>
											{isPhone && (
												<SearchInput
													searchValue={value}
													setSearchValue={
														handleInputChange
													}
													autoFocus={true}
													alwaysLight={alwaysLight}
													isLocationAutocomplete
													isLanding={isLanding}
												/>
											)}
											<Block py={2}>
												{/* Wyświetla Listę Miast Spełniajacych Kryteria */}
												{result?.city?.length >= 5 &&
													renderModalCities()}
												{/* W zależności od liczby wyników i props'a "noOther" wyświetla opcję "Inne" */}
												{result?.city?.length > 0 &&
													result?.city?.length <
														5 && (
														<>
															{renderModalCities()}
															{renderOtherModalItem()}
														</>
													)}
												{result?.city?.length === 0 &&
													renderOtherModalItem()}
												{/* Jeśli 'extend === true' - Wyświetla Pod Lista Miast Również Region i Kraj  */}
												{extend && renderModalExtend()}
												{/* Jeśli 'withEnterTheCityLabel === true' - Wyświetla Tekst - 'Wpisz Miejscowość'  */}
												{withEnterTheCityLabel && (
													<ModalEnterCityLabelItem />
												)}
												{/* Komunikat Na Brak Wyników Spełniajacych Kryteria Wyszukiwania  */}
												{noOther &&
													result?.city?.length ===
														0 &&
													result?.region?.length ===
														0 &&
													result?.country?.length ===
														0 && (
														<ModalNoResultsItem
															close={() => {
																setValue(
																	beforeFocusLocationValue
																);
																setBeforeFocusLocationValue(
																	""
																);
																extendedClose();
															}}
														/>
													)}
											</Block>
										</>
									) : (
										<Block
											flex
											justifyCenter
											alignCenter
											height={40}
										>
											<Loader size={20} />
										</Block>
									)}
								</Block>
							</Block>
						)
					}
				>
					<DropdownTriggerContext.Consumer>
						{({ triggerProps, layerSide }) => (
							<LocationActivator
								value={isOpen && isPhone ? "" : value}
								isSelected={isSelected}
								label={label}
								innerRef={triggerProps.ref}
								onFocus={onFocus}
								handleInputChange={
									isOpen && isPhone ? null : handleInputChange
								}
								inputComponent={inputComponent}
								inputComponentProps={inputComponentProps}
								minWidth={minWidth}
								activatorOptions={{
									noMaxWidth,
									noPad,
									noBold: isOpen && isPhone ? true : noBold,
									withGeolocation,
									isLanding,
									hideLabel,
								}}
								showDistance={showDistance}
								isBan={isBan}
								isOpen={isOpen}
								open={extendedOpen}
								close={extendedClose}
								alwaysLight={alwaysLight}
								fetchInitialResult={fetchInitialResult}
								setValue={setValue}
								layerSide={layerSide}
							/>
						)}
					</DropdownTriggerContext.Consumer>
				</Dropdown>
			</Block>

			{/* Wybór dystansu wyszukiwania */}
			{showDistance && (
				<DistanceSelector
					location={location}
					handleLocationSelect={handleLocationSelect}
					subscribeActualOpenSelectorEvent={
						subscribeActualOpenSelectorEvent
					}
					customMobileLayerStyle={customMobileLayerStyle}
					activatorStyleOptions={{ noFullWidth: true }}
					useLayerOptions={useLayerOptions}
					noPad={noPad}
				/>
			)}
		</Block>
	) : (
		<OtherLocations
			handleLocationSelect={handleLocationSelect}
			selectorProps={selectorProps}
			hideLabel={hideLabel}
			isKeyboardOpen={isKeyboardOpen}
			visualViewportHeight={visualViewportHeight}
			customMobileLayerStyle={{
				display: "flex",
				flexDirection: "column",
			}}
			isSingleDropdown={true}
			isPhone={isPhone}
			isRegister={inputComponentProps?.register}
		/>
	);
};

// #####################################################

export default LocationAutocomplete;

// #####################################################
